import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw, Trash2, RotateCcw, Cloud, CloudOff, KeyRound, Camera } from 'lucide-react';
import {
  getSyncQueue, getDeadLetter,
  getPhotoQueue, getPhotoDeadLetter,
  removeFromSyncQueue, clearSyncQueue,
  removeFromDeadLetter, retryFromDeadLetter, clearDeadLetter,
  removeFromPhotoQueue, removeFromPhotoDeadLetter, clearPhotoDeadLetter,
  getLastSuccessfulSync,
  type SyncQueueItem, type PhotoQueueItem,
} from '@/lib/offlineStorage';
import { processSyncQueue } from '@/lib/syncEngine';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const TABLE_LABELS: Record<string, string> = {
  food_logs: '🍽️ Comida registrada',
  exercise_logs: '🏃 Cardio/ejercicio',
  exercise_weights: '🏋️ Peso de ejercicio',
  routine_completion_logs: '✅ Confirmación de entreno',
  workout_session: '☑️ Marca de ejercicio',
  body_measurements: '⚖️ Medición corporal',
  progress_photos: '📷 Foto de progreso',
  client_meal_plans: '🍱 Plan alimenticio',
  supplements: '💊 Suplemento',
};

const ACTION_LABELS: Record<string, string> = {
  insert: 'Crear',
  update: 'Actualizar',
  upsert: 'Crear/Actualizar',
  delete: 'Eliminar',
};

const POSE_LABELS: Record<string, string> = {
  front: 'Frente',
  left: 'Perfil izq.',
  right: 'Perfil der.',
  back: 'Espalda',
};

function summarizeData(item: SyncQueueItem): string {
  const d = item.data as Record<string, any>;
  if (item.table === 'food_logs') {
    return `${d.item_name || '?'} (${d.calories || 0} kcal, ${d.meal_type || ''})`;
  }
  if (item.table === 'exercise_weights') {
    return `${d.exercise_name || '?'}: ${d.weight_value || 0} ${d.weight_unit || ''}`;
  }
  if (item.table === 'workout_session') {
    return `${d.exercise_name || '?'} (${d.day_name || ''})`;
  }
  if (item.table === 'routine_completion_logs') {
    return `${d.day_name || '?'} - ${d.completion_percentage || 0}%`;
  }
  if (item.table === 'exercise_logs') {
    return `${d.description || d.exercise_type || '?'} (${d.calories_burned || 0} kcal)`;
  }
  return JSON.stringify(d).slice(0, 80);
}

function formatDate(ts?: number | null): string {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function formatRelativeTime(ts: number): string {
  const diffSec = Math.floor((Date.now() - ts) / 1000);
  if (diffSec < 60) return 'hace unos segundos';
  if (diffSec < 3600) return `hace ${Math.floor(diffSec / 60)} min`;
  if (diffSec < 86400) return `hace ${Math.floor(diffSec / 3600)} h`;
  return `hace ${Math.floor(diffSec / 86400)} d`;
}

export function SyncQueueDialog({ open, onOpenChange }: Props) {
  const [pending, setPending] = useState<SyncQueueItem[]>([]);
  const [dead, setDead] = useState<SyncQueueItem[]>([]);
  const [photoPending, setPhotoPending] = useState<PhotoQueueItem[]>([]);
  const [photoDead, setPhotoDead] = useState<PhotoQueueItem[]>([]);
  const [lastSync, setLastSync] = useState<number | null>(null);
  const [needsReAuth, setNeedsReAuth] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [tab, setTab] = useState<'pending' | 'dead'>('pending');

  const refresh = () => {
    const p = getSyncQueue();
    const d = getDeadLetter();
    const pp = getPhotoQueue();
    const pd = getPhotoDeadLetter();
    setPending(p);
    setDead(d);
    setPhotoPending(pp);
    setPhotoDead(pd);
    setLastSync(getLastSuccessfulSync());
    // If any pending item was tagged needsReAuth, flag the banner.
    setNeedsReAuth([...p, ...pp].some(i => (i as any).needsReAuth));
  };

  useEffect(() => {
    if (open) refresh();
  }, [open]);

  const handleRetryAll = async () => {
    setSyncing(true);
    const { data: { session } } = await supabase.auth.getSession();
    const result = await processSyncQueue(session?.user?.id);
    setSyncing(false);
    refresh();
    if (result.synced > 0) toast.success(`${result.synced} cambio(s) sincronizado(s)`);
    if (result.needsReAuth) {
      toast.error('Tu sesión expiró — vuelve a iniciar sesión y reintenta.');
    } else if (result.movedToDead > 0) {
      toast.error(`${result.movedToDead} cambio(s) movidos a "no se pudieron sincronizar"`);
    } else if (result.failed > 0 && result.synced === 0) {
      toast.warning(`${result.failed} siguen pendientes — revisa el detalle del error.`);
    }
  };

  const handleRetryDead = (id: string) => {
    retryFromDeadLetter(id);
    refresh();
    toast.info('Movido a la cola — se intentará en el próximo sync');
  };

  const handleDeleteDead = (id: string) => {
    removeFromDeadLetter(id);
    refresh();
    toast.success('Eliminado');
  };

  const handleDeletePhoto = (id: string, isDead: boolean) => {
    if (isDead) removeFromPhotoDeadLetter(id);
    else removeFromPhotoQueue(id);
    refresh();
    toast.success('Foto eliminada de la cola');
  };

  const handleClearAllDead = () => {
    clearDeadLetter();
    clearPhotoDeadLetter();
    refresh();
    toast.success('Lista de fallos limpiada');
  };

  const handleDeletePending = (id: string) => {
    removeFromSyncQueue(id);
    refresh();
    toast.success('Item eliminado de la cola');
  };

  const handleClearAllPending = () => {
    if (!confirm('¿Eliminar TODOS los cambios pendientes? Si los cambios todavía no se subieron al servidor, se perderán para siempre. Esta acción no se puede deshacer.')) {
      return;
    }
    clearSyncQueue();
    // Also clear all photos pending
    getPhotoQueue().forEach(p => removeFromPhotoQueue(p.id));
    refresh();
    toast.success('Cola de pendientes vaciada');
  };

  const showPending = tab === 'pending';
  const dataItems = showPending ? pending : dead;
  const photoItems = showPending ? photoPending : photoDead;
  const totalThisTab = dataItems.length + photoItems.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {navigator.onLine ? <Cloud className="h-5 w-5" /> : <CloudOff className="h-5 w-5 text-orange-500" />}
            Cambios pendientes de sincronizar
          </DialogTitle>
          <DialogDescription>
            {navigator.onLine
              ? 'Estás en línea. Los cambios se sincronizan automáticamente.'
              : 'Estás sin conexión. Los cambios se guardan aquí hasta que vuelvas a tener internet.'}
          </DialogDescription>
        </DialogHeader>

        {/* Re-auth banner if any item needs login */}
        {needsReAuth && (
          <div className="rounded-lg border border-orange-500/30 bg-orange-500/10 p-3 flex items-start gap-2">
            <KeyRound className="h-4 w-4 text-orange-500 shrink-0 mt-0.5" />
            <div className="text-xs">
              <div className="font-bold text-orange-600 dark:text-orange-400">
                Tu sesión necesita renovarse
              </div>
              <div className="text-muted-foreground mt-0.5">
                Algunos cambios fallaron porque tu sesión expiró. Cierra sesión y vuelve a entrar para sincronizar.
              </div>
            </div>
          </div>
        )}

        {/* Last sync info */}
        <div className="text-xs text-muted-foreground">
          {lastSync
            ? `Última sincronización exitosa: ${formatDate(lastSync)} (${formatRelativeTime(lastSync)})`
            : 'Aún no se ha sincronizado nada en este dispositivo.'}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b">
          <button
            onClick={() => setTab('pending')}
            className={`flex-1 py-2 text-sm font-bold uppercase tracking-wider min-h-[44px] ${
              showPending ? 'text-accent border-b-2 border-accent' : 'text-muted-foreground'
            }`}
          >
            Pendientes ({pending.length + photoPending.length})
          </button>
          <button
            onClick={() => setTab('dead')}
            className={`flex-1 py-2 text-sm font-bold uppercase tracking-wider min-h-[44px] ${
              !showPending ? 'text-destructive border-b-2 border-destructive' : 'text-muted-foreground'
            }`}
          >
            No sincronizables ({dead.length + photoDead.length})
          </button>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto -mx-6 px-6 py-3 space-y-2">
          {totalThisTab === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              {showPending
                ? '🎉 Todo está sincronizado'
                : '✅ Sin cambios fallidos permanentemente'}
            </div>
          ) : (
            <>
              {/* Photo items */}
              {photoItems.map(item => (
                <div key={item.id} className="border rounded-lg p-3 bg-card flex gap-3">
                  <div className="shrink-0 w-14 h-14 rounded overflow-hidden bg-muted flex items-center justify-center">
                    <img
                      src={`data:${item.contentType};base64,${item.base64}`}
                      alt="preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="font-bold text-sm flex items-center gap-1">
                        <Camera className="w-3.5 h-3.5 inline" />
                        Foto: {POSE_LABELS[item.poseType] || item.poseType}
                      </div>
                      <button
                        onClick={() => handleDeletePhoto(item.id, !showPending)}
                        className="p-2 rounded hover:bg-destructive/10 text-destructive shrink-0"
                        title="Eliminar"
                        aria-label="Eliminar"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="text-xs text-muted-foreground">{item.photoMonth}</div>
                    <div className="text-[10px] text-muted-foreground mt-1 flex flex-wrap gap-x-3">
                      <span>Tomada: {formatDate(item.timestamp)}</span>
                      {item.attempts ? <span>Intentos: {item.attempts}</span> : null}
                    </div>
                    {item.lastError && (
                      <div className="text-[10px] text-destructive mt-1 flex items-start gap-1">
                        <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
                        <span className="break-words">{item.lastError}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* Data items */}
              {dataItems.map(item => (
                <div key={item.id} className="border rounded-lg p-3 bg-card">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="font-bold text-sm">
                      {TABLE_LABELS[item.table] || item.table} —{' '}
                      <span className="text-muted-foreground font-normal">
                        {ACTION_LABELS[item.action] || item.action}
                      </span>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      {!showPending && (
                        <button
                          onClick={() => handleRetryDead(item.id)}
                          className="p-2 rounded hover:bg-accent/10 text-accent"
                          title="Reintentar"
                          aria-label="Reintentar"
                        >
                          <RotateCcw className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => showPending ? handleDeletePending(item.id) : handleDeleteDead(item.id)}
                        className="p-2 rounded hover:bg-destructive/10 text-destructive"
                        title="Eliminar de la cola"
                        aria-label="Eliminar"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{summarizeData(item)}</div>
                  <div className="text-[10px] text-muted-foreground mt-1 flex flex-wrap gap-x-3">
                    <span>Creado: {formatDate(item.timestamp)}</span>
                    {item.attempts !== undefined && item.attempts > 0 && (
                      <span>Intentos: {item.attempts}</span>
                    )}
                    {item.lastAttemptAt && (
                      <span>Último intento: {formatDate(item.lastAttemptAt)}</span>
                    )}
                  </div>
                  {item.lastError && (
                    <div className="text-[10px] text-destructive mt-1 flex items-start gap-1">
                      <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
                      <span className="break-words">{item.lastError}</span>
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
          {showPending && (pending.length + photoPending.length) > 0 && (
            <>
              <Button onClick={handleRetryAll} disabled={syncing || !navigator.onLine} className="gap-2">
                <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Sincronizando…' : 'Reintentar todo'}
              </Button>
              <Button onClick={handleClearAllPending} variant="outline" className="gap-2 text-destructive border-destructive/30 hover:bg-destructive/10">
                <Trash2 className="h-4 w-4" />
                Borrar todo
              </Button>
            </>
          )}
          {!showPending && (dead.length + photoDead.length) > 0 && (
            <Button onClick={handleClearAllDead} variant="destructive" className="gap-2">
              <Trash2 className="h-4 w-4" />
              Limpiar todos los fallidos
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
